import { cn } from '~/lib/utils';
import styles from './DayDivider.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface DayDividerProps {
  /** The break itself — "Today", "Yesterday", "12 March". Also the separator's accessible name. */
  label: string;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The day break in a thread, as drawn in *Founder · cold start*. A centred 11/16 label between two 1px rules in a 28px box.
 *
 * It is the only divider in v1.5: menu sections are introduced by a label and never by a rule,
 * tables rule only under their header, and turns are separated by space alone. A date earns one
 * because it is a mark *on* the thread rather than a message *in* it.
 *
 * `role="separator"` with the label as its accessible name, so assistive tech gets the same "new
 * day starts here" that the rules give a sighted reader — a styled `div` would announce a bare
 * "Today" indistinguishable from a message that happened to say it.
 */
export function DayDivider({ label, theme: themeProp, className }: DayDividerProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div
      className={cn(chatThemeClass(theme), styles.root, className)}
      role="separator"
      aria-label={label}
    >
      <span className={styles.rule} aria-hidden="true" />
      <span className={styles.label}>{label}</span>
      <span className={styles.rule} aria-hidden="true" />
    </div>
  );
}
